import * as amplitude from "@amplitude/analytics-browser";
import { useCheckout } from "@stripe/react-stripe-js/checkout";
import { StripeExpressCheckoutElementConfirmEvent } from "@stripe/stripe-js";
import Lottie from "lottie-react";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { LoadingDots } from "../../LandingPage";
import loadingdots from "../../lottiedata/loadingdots.json";
import { CreditCardForm } from "./CreditCardForm";

const PayWithCardButton = styled.button`
  width: 100%;
  background: #0b6dff;
  color: white;
  border: 2px solid #e9ecef;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 16px;

  &:hover {
    background: rgb(40, 126, 255);
  }

  &:focus {
    outline: none;
    border-color: #0b6dff;
    box-shadow: 0 0 0 2px rgba(11, 109, 255, 0.1);
  }
`;

type CheckoutFormProps = {
  /** web34_checkout_cta: forwarded to CreditCardForm's submit label. */
  isWeb34Variation?: boolean;
};

export const CheckoutForm = ({ isWeb34Variation = false }: CheckoutFormProps) => {
  const { t } = useTranslation();
  const checkout = useCheckout();
  const [showCardForm, setShowCardForm] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(true);
  const isCheckoutMounted = useRef(false);
  const checkoutRef = useRef(checkout);

  useEffect(() => {
    checkoutRef.current = checkout;
  }, [checkout]);

  // Handle Express Checkout (Apple Pay, Google Pay, etc.)
  // Defined before useEffect so it can be referenced, uses refs to avoid stale closures
  const handleExpressConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      try {
        const currentCheckout = checkoutRef.current;
        if (currentCheckout.type === "success") {
          await currentCheckout.checkout.confirm({
            expressCheckoutConfirmEvent: event,
          });
        }
      } catch (err) {
        console.error("Error during express checkout confirmation:", err);
      }
    },
    []
  );

  useEffect(() => {
    if (checkout.type === "success" && !isCheckoutMounted.current) {
      isCheckoutMounted.current = true;

      const ece = checkout.checkout.createExpressCheckoutElement();
      ece.on("confirm", handleExpressConfirm);
      ece.mount("#express-checkout-element");
      ece.on("ready", () => {
        setShowLoadingSpinner(false);
      });
      ece.on("loaderror", (event: any) => {
        setShowLoadingSpinner(false);
        console.error("Express Checkout failed:", event.error.message);
      });
      setTimeout(() => {
        setShowLoadingSpinner(false);
      }, 5000);
    }
  }, [checkout, handleExpressConfirm]);

  const handlePayWithCard = useCallback(() => {
    setShowCardForm(true);
    amplitude.track("PayWithCardClicked");
  }, []);

  const isLoading = showLoadingSpinner;

  return (
    <>
      {/* Payment methods with loading state */}
      {isLoading && (
        <LoadingDots>
          <Lottie autoplay animationData={loadingdots} />
        </LoadingDots>
      )}
      <div style={{ display: isLoading ? "none" : "block" }}>
        <div id="express-checkout-element" />
        <div>
          {!showCardForm ? (
            <PayWithCardButton onClick={handlePayWithCard}>
              {t("checkout.card.continueWithCard")}
            </PayWithCardButton>
          ) : (
            <CreditCardForm isWeb34Variation={isWeb34Variation} />
          )}
        </div>
      </div>
    </>
  );
};
